import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedFetch } from '../../../hooks/useAuth'
import type { NetworkReviewBaselineResult } from '../models/ReviewBaseline'

export const networkReviewBaselineKeys = {
  folder: (folderId: string | null | undefined) => ['network-review-baseline', folderId] as const,
}

export function useNetworkReviewBaseline(folderId: string | null | undefined) {
  const fetcher = useAuthenticatedFetch()
  return useQuery<NetworkReviewBaselineResult | null>({
    queryKey: networkReviewBaselineKeys.folder(folderId),
    enabled: Boolean(folderId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!folderId) return null
      const response = await fetcher(
        `/api/network/folders/${encodeURIComponent(folderId)}/review-baseline`,
      )
      if (!response.ok) return null
      return response.json() as Promise<NetworkReviewBaselineResult>
    },
  })
}

export function useAcceptNetworkReviewBaseline(folderId: string | null | undefined) {
  const fetcher = useAuthenticatedFetch()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<NetworkReviewBaselineResult> => {
      if (!folderId) throw new Error('Missing folder id')
      const response = await fetcher(
        `/api/network/folders/${encodeURIComponent(folderId)}/review-baseline/accept`,
        { method: 'POST' },
      )
      if (!response.ok) {
        const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
        throw new Error(body.error || `HTTP ${response.status}`)
      }
      return response.json() as Promise<NetworkReviewBaselineResult>
    },
    onSuccess: (result) => {
      queryClient.setQueryData(networkReviewBaselineKeys.folder(folderId), result)
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      void queryClient.invalidateQueries({ queryKey: ['compass', 'folder-activity'] })
    },
  })
}
